import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, Table, Input, InputNumber, Button, Space, message, Alert, Tag, Popconfirm } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { DoctorQuotaItem, getProjectDoctorQuotas, updateProjectDoctorQuotas } from '../services/project';

interface DoctorQuotaDrawerProps {
  open: boolean;
  projectId: number | null;
  projectName?: string;
  onClose: () => void;
}

const PAGE_SIZE = 20;

const DoctorQuotaDrawer: React.FC<DoctorQuotaDrawerProps> = ({ open, projectId, projectName, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState<DoctorQuotaItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [batchValue, setBatchValue] = useState<number | null>(null);
  // 记录被修改过的配额，key 为医生ID，值为 null 表示取消限制
  const [edits, setEdits] = useState<Record<number, number | null>>({});

  const fetchList = useCallback(async (targetPage: number, targetKeyword: string) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await getProjectDoctorQuotas(projectId, {
        page: targetPage,
        pageSize: PAGE_SIZE,
        keyword: targetKeyword
      });
      if (res.success) {
        setList(res.data.list || []);
        setTotal(res.data.total || 0);
      } else {
        message.error(res.message || '获取医生配额失败');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || '获取医生配额失败');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open && projectId) {
      setPage(1);
      setKeyword('');
      setSearchInput('');
      setSelectedKeys([]);
      setBatchValue(null);
      setEdits({});
      fetchList(1, '');
    }
  }, [open, projectId, fetchList]);

  const currentValue = (record: DoctorQuotaItem): number | null =>
    Object.prototype.hasOwnProperty.call(edits, record.doctor_id)
      ? edits[record.doctor_id]
      : record.max_submissions;

  const handleValueChange = (doctorId: number, value: number | null) => {
    setEdits(prev => ({ ...prev, [doctorId]: value }));
  };

  const handleSearch = () => {
    setPage(1);
    setKeyword(searchInput.trim());
    fetchList(1, searchInput.trim());
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchList(nextPage, keyword);
  };

  const applyBatch = () => {
    if (selectedKeys.length === 0) {
      message.warning('请先勾选需要设置的医生');
      return;
    }
    const next: Record<number, number | null> = { ...edits };
    selectedKeys.forEach(key => {
      next[Number(key)] = batchValue;
    });
    setEdits(next);
    message.success(
      batchValue === null
        ? `已将 ${selectedKeys.length} 位医生标记为不限制，保存后生效`
        : `已将 ${selectedKeys.length} 位医生的上限设为 ${batchValue}，保存后生效`
    );
  };

  const handleSave = async () => {
    if (!projectId) return;
    const quotas = Object.entries(edits).map(([doctorId, value]) => ({
      doctor_id: Number(doctorId),
      max_submissions: value
    }));

    if (quotas.length === 0) {
      message.info('没有需要保存的修改');
      return;
    }

    setSaving(true);
    try {
      const res = await updateProjectDoctorQuotas(projectId, quotas);
      if (res.success) {
        message.success(res.message || '配额保存成功');
        setEdits({});
        setSelectedKeys([]);
        fetchList(page, keyword);
      } else {
        message.error(res.message || '配额保存失败');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || '配额保存失败');
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<DoctorQuotaItem> = [
    {
      title: '医生',
      key: 'doctor',
      width: 160,
      render: (_, record) => (
        <div>
          <div>{record.doctor_name}</div>
          <div style={{ color: '#999', fontSize: 12 }}>
            {record.doctor_phone}
            {record.id_card_tail ? ` / 证件尾号${record.id_card_tail}` : ''}
          </div>
        </div>
      )
    },
    {
      title: '医院 / 科室',
      key: 'hospital',
      width: 200,
      render: (_, record) => (
        <div>
          <div>{record.hospital_name || '-'}</div>
          <div style={{ color: '#999', fontSize: 12 }}>
            {record.department_name || '-'}
            {record.title ? ` / ${record.title}` : ''}
          </div>
        </div>
      )
    },
    {
      title: '已提交',
      dataIndex: 'submitted_count',
      key: 'submitted_count',
      width: 90,
      render: (count: number) => <Tag color={count > 0 ? 'blue' : 'default'}>{count} 次</Tag>
    },
    {
      title: '提交次数上限',
      key: 'max_submissions',
      width: 200,
      render: (_, record) => {
        const value = currentValue(record);
        const changed = Object.prototype.hasOwnProperty.call(edits, record.doctor_id);
        const exceeded = value !== null && record.submitted_count > value;
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <InputNumber
              min={0}
              precision={0}
              style={{ width: '100%' }}
              value={value ?? undefined}
              placeholder="留空表示不限制"
              onChange={val => handleValueChange(record.doctor_id, val === null || val === undefined ? null : Number(val))}
            />
            {changed && <span style={{ color: '#faad14', fontSize: 12 }}>待保存</span>}
            {exceeded && (
              <span style={{ color: '#ff4d4f', fontSize: 12 }}>
                已提交次数超过该上限，该医生将无法继续提交
              </span>
            )}
          </Space>
        );
      }
    }
  ];

  const pendingCount = Object.keys(edits).length;

  return (
    <Drawer
      title={`医生提交次数配额${projectName ? ` - ${projectName}` : ''}`}
      open={open}
      onClose={onClose}
      width={900}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>取消</Button>
          {pendingCount > 0 ? (
            <Popconfirm
              title={`确认保存 ${pendingCount} 位医生的配额修改？`}
              onConfirm={handleSave}
              okText="保存"
              cancelText="再想想"
            >
              <Button type="primary" loading={saving}>
                保存修改（{pendingCount}）
              </Button>
            </Popconfirm>
          ) : (
            <Button type="primary" disabled>
              保存修改
            </Button>
          )}
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="配额说明"
        description="这里设置的是每位医生在本项目内跨所有问卷的累计提交次数上限。留空表示该医生不受此限制。上限与问卷自身的次数限制同时生效，任一达到上限即无法继续提交。"
      />

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="按姓名 / 手机号 / 医院搜索"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 240 }}
          allowClear
        />
        <Button icon={<SearchOutlined />} onClick={handleSearch}>
          搜索
        </Button>
      </Space>

      <Space style={{ marginBottom: 16 }} wrap>
        <span>批量设置选中项：</span>
        <InputNumber
          min={0}
          precision={0}
          value={batchValue ?? undefined}
          placeholder="留空为不限制"
          onChange={val => setBatchValue(val === null || val === undefined ? null : Number(val))}
          style={{ width: 160 }}
        />
        <Button onClick={applyBatch} disabled={selectedKeys.length === 0}>
          应用到选中的 {selectedKeys.length} 位
        </Button>
      </Space>

      <Table<DoctorQuotaItem>
        rowKey="doctor_id"
        columns={columns}
        dataSource={list}
        loading={loading}
        size="small"
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: keys => setSelectedKeys(keys)
        }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          showTotal: t => `共 ${t} 位白名单医生`,
          onChange: handlePageChange
        }}
      />
    </Drawer>
  );
};

export default DoctorQuotaDrawer;
